import React, { FunctionComponent, useContext, useEffect, useState } from "react";
import { v4 as uuidv4 } from 'uuid';

// OL
import { transform } from 'ol/proj';
import OlFeature from 'ol/Feature';
import OlLineString from 'ol/geom/LineString';
import OlFormatWKB from 'ol/format/WKB';

// Custom
import DisplayRouteControlRoot from "./DisplayRouteControlRoot";
import useRouting from "@/components/MapRouting/useRouting";
import { prepareCoordinatesForDisplay } from '@/lib/olHelpers';
import { DCRecord } from '@/@types/lib/dataController';
import MapContext from "@/components/Map/MapContext";

// Services
import mapService from '@/services/mapService';

// Types
import { RoutePoint } from '@/components/MapSidebarPanes/RoutePaneV2';
import { MapContextType } from "@/@types/context/MapContext";


export interface IDisplayRouteControl {
  id: string
  order?: number
  target?: HTMLElement
  initialRecordRoutePoints?: DCRecord[]
}

const LINE = 'LineString';

const DisplayRouteControl: FunctionComponent<IDisplayRouteControl> = (props) => {

  const mapContext = useContext(MapContext) as MapContextType;

  const [initialRecordSet, setInitialRecordSet] = useState(false);

  const { routeData, routingPoints, loadRoutingPoints, drawRoute } = useRouting({});
  
  const { initialRecordRoutePoints } = props;

  useEffect(() => {
    if (!initialRecordSet) {
      if (initialRecordRoutePoints && Array.isArray(initialRecordRoutePoints)) {
        const routePointsParsedCoordinates = initialRecordRoutePoints
          .sort((rp1, rp2) => (rp1.pt_order as number) - (rp2.pt_order as number))
          .filter((rp) => !!rp.wkt)
          .map((rp) => {
            // Extract coordinates from WKT
            const match = (rp.wkt as string).match(/POINT\((-?\d+\.?\d*) (-?\d+\.?\d*)\)/);
            let coords = [0, 0];

            if (match) {
              coords = [parseFloat(match[1]), parseFloat(match[2])];
            }

            // Convert coords from EPSG:3765 to EPSG:3857
            const transformedCoords = transform(coords, 'EPSG:3765', 'EPSG:3857');
            const displayCoords = prepareCoordinatesForDisplay(transformedCoords);
            return {
              poi_id: rp.poi_id as number, // use this to find poi id once poi layer starts working
              coord: transformedCoords as number[],
              label: rp.poi_label || `${displayCoords[1]}N ${displayCoords[0]}E`,
              uuid: uuidv4(),
            } as RoutePoint;
          });

        loadRoutingPoints(routePointsParsedCoordinates);

        setInitialRecordSet(true);
      }
    }
  }, [initialRecordRoutePoints]);

  useEffect(() => {
    if (routeData) {

      const extent = routeData.feature.getGeometry()?.getExtent();
      if (mapContext.map && extent) {
        mapContext.map.getView().fit(extent);
      }
    }
  }, [routeData]);

  useEffect(() => {
    // localStorage.setItem('route_points', JSON.stringify(routingPoints));

    // handleClear();

    if (mapService.getRoute && routingPoints.length > 1) {
      // loaderContext.toggleLoading(true);
      // setIsLoading(true);

      const lineStringGeometry = new OlLineString(routingPoints.map((x: RoutePoint) => x.coord));

      if (lineStringGeometry instanceof OlLineString) {
        const lineStringFeature = new OlFeature({
          type: LINE,
          geometry: lineStringGeometry,
        });

        mapService
          .getRoute(lineStringFeature)
          .then((ri) => {
            // features for the route linestring
            if (ri === null) {
              // snackbarContext.showNotification(t('notifications.no_route_found'), 'error');
              return;
            }
            const wktFormatter = new OlFormatWKB();
            const retrievedRecords = ri as DCRecord[];

            if (Array.isArray(retrievedRecords) && retrievedRecords.length > 0) {
              const rec = retrievedRecords[0];

              const geom = wktFormatter.readGeometry(rec.wkb as string, {
                dataProjection: 'EPSG:3765',
                featureProjection: 'EPSG:3857',
              });
              const feat: OlFeature<OlLineString> = new OlFeature<OlLineString>({
                type: LINE,
                geometry: geom as OlLineString,
              });
              feat.set('recordId', rec.id);
              feat.set('code', rec.code);

              drawRoute({
                record: rec,
                feature: feat
              });
            }
          })
          .catch(() => {
            // snackbarContext.showNotification(t("notifications.no_route_found"), "error");
          })
          .finally(() => {
            // loaderContext.toggleLoading(false);
            // setIsLoading(false);
          });
      }
    }
  }, [routingPoints]);



  return <DisplayRouteControlRoot {...props} />
};

export default DisplayRouteControl;

